<template>
  <div :data-testId="`${codeAreaKey}-code-editor-wrapper`" class="tw-w-full">
    <p v-if="title" class="tw-text-2xl">{{ title }}</p>
    <div class="tw-mt-2 tw-w-3/4" :class="accordionIsExpanded ? 'tw-mb-8' : 'tw-mb-2'">
      <div v-html="description" class="tw-text-sm" :class="parametersDescription ? 'tw-mb-2' : 'tw-mb-4'"></div>
      <StudioAccordion
        v-if="parametersDescription"
        :isExpanded="accordionIsExpanded"
        @onToggleAccordion="accordionIsExpanded = !accordionIsExpanded"
      >
        <template #head>
          <p>Available Data Points</p>
        </template>
        <template #details>
          <div v-html="parametersDescription" class="tw-text-sm"></div>
        </template>
      </StudioAccordion>
    </div>
    <StudioCodeEditor
      :ref="codeAreaKey"
      :cmContainerId="'cm_' + codeAreaKey"
      :doc="doc"
      :characterLimit="characterLimit"
      :addedExtensions="getExtensionList()"
      :useBasicSetup="true"
      :language="language"
      @viewCreated="handleViewCreated"
      @stateCreated="handleStateCreated"
      @change="handleDocChange"
    />
    <div v-if="shouldShowEditScriptCTA" class="tw-mt-1">
      <StudioButton
        v-if="!editMode"
        :disabled="editDisabled"
        :trackingId="`edit-${codeAreaKey}`"
        variant="outline"
        @click="handleEdit"
      >
        <template #icon>
          <TablerEdit />
        </template>
        Edit
      </StudioButton>
      <div v-else class="tw-flex tw-justify-start tw-space-x-2">
        <StudioButton variant="outline" :trackingId="`cancel-${codeAreaKey}`" @click="handleCancel">
          Cancel
        </StudioButton>
        <StudioButton
          :loading="saveLoading"
          :disabled="!hasChanged || saveLoading"
          :trackingId="`save-${codeAreaKey}`"
          @click="handleSave"
        >
          Save
        </StudioButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { forEachDiagnostic, Diagnostic, forceLinting } from '@codemirror/lint';
import { EditorState, Extension, Compartment } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { PropType, defineComponent, markRaw } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import { restrictedKeywordsCheck, restrictedTagsCheck, readOnlyExtension } from './marketingTags.customExtensions';
import { ReadOnlyRange, Language, CodeAreaKeys } from './store/types';

import TablerEdit from '@/studioComponents/Icons/tabler/TablerEdit.vue';
import StudioAccordion from '@/studioComponents/StudioAccordion.vue';
import StudioButton from '@/studioComponents/StudioButton.vue';
import StudioCodeEditor from '@/studioComponents/StudioCodeEditor.vue';
import { processErrorParent } from '@/utils/utils';

export default defineComponent({
  name: 'CodeEditorWrapper',
  components: { StudioCodeEditor, StudioButton, TablerEdit, StudioAccordion },
  props: {
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    parametersDescription: {
      type: String,
      default: ''
    },
    editDisabled: {
      type: Boolean,
      default: false
    },
    codeAreaKey: {
      type: String as PropType<CodeAreaKeys>,
      required: true
    },
    doc: {
      type: String,
      default: ''
    },
    language: {
      type: String as PropType<Language>,
      default: 'html'
    },
    readOnlyRanges: {
      type: Array as () => ReadOnlyRange[],
      default: () => []
    },
    restrictedTags: {
      type: Array as () => string[],
      default: () => []
    },
    restrictedKeywords: {
      type: Array as () => string[],
      default: () => []
    },
    characterLimit: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      editMode: false,
      saveLoading: false,
      hasChanged: false,
      accordionIsExpanded: false,
      editorView: {} as EditorView,
      initialState: {} as EditorState,
      themeCompartment: new Compartment(),
      readOnlyCompartment: new Compartment(),
      editTheme: {
        '&': {
          backgroundColor: '#ffffff'
        }
      },
      viewTheme: {
        '&': {
          backgroundColor: '#dddddd',
          border: '1px solid #d1d5db',
          pointerEvents: 'none'
        }
      },
      errorTheme: {
        '&': {
          border: '3px solid red',
          backgroundColor: '#ffffff'
        }
      }
    };
  },
  computed: {
    ...mapGetters('org', {
      getIsWorkspaceEnabled: 'getIsWorkspaceEnabled'
    }),
    ...mapGetters('user', {
      isCurrentUserWorkspaceAdmin: 'isCurrentUserWorkspaceAdmin'
    }),
    shouldShowEditScriptCTA(): boolean {
      if (
        this.$route.name === 'JavascriptEmbedSettings' &&
        this.getIsWorkspaceEnabled &&
        this.isCurrentUserWorkspaceAdmin
      ) {
        return false;
      }

      return true;
    }
  },
  methods: {
    ...mapActions('studioToast', { triggerToast: 'triggerToast' }),
    getExtensionList(): Extension[] {
      const extensions = [] as Extension[];
      extensions.push(this.themeCompartment.of(EditorView.theme(this.viewTheme)));

      if (this.restrictedTags.length) {
        extensions.push(restrictedTagsCheck(this.restrictedTags));
      }

      if (this.restrictedKeywords.length) {
        extensions.push(restrictedKeywordsCheck(this.restrictedKeywords));
      }

      const readOnlyExt = readOnlyExtension([{ fromLine: 1, toLine: 'last' }]);
      extensions.push(this.readOnlyCompartment.of(readOnlyExt));

      return extensions;
    },
    handleDocChange(newDoc) {
      this.hasChanged = newDoc !== this.initialState.doc.toString();
    },
    setTheme(theme) {
      this.editorView.dispatch({
        effects: this.themeCompartment.reconfigure(EditorView.theme(theme))
      });
    },
    setReadOnlyRanges(ranges: ReadOnlyRange[]) {
      this.editorView.dispatch({
        effects: this.readOnlyCompartment.reconfigure(readOnlyExtension(ranges))
      });
    },
    handleViewCreated(view) {
      this.editorView = markRaw(view);
      this.$emit('viewCreated', view);
    },
    handleStateCreated(state) {
      this.initialState = markRaw(state);
    },
    handleEdit() {
      this.editMode = true;
      this.setTheme(this.editTheme);
      this.setReadOnlyRanges(this.readOnlyRanges);
      this.$emit('edit');
    },
    handleCancel() {
      this.editMode = false;
      this.editorView.setState(this.initialState as EditorState);
      this.$emit('cancel');
    },
    async handleSave() {
      this.saveLoading = true;
      if (this.characterLimitExceeded()) {
        this.setTheme(this.errorTheme);
        this.saveLoading = false;
        return;
      }
      const diagnostics = await this.getDiagnostics();
      if (diagnostics.length > 0) {
        this.triggerToast({
          mode: 'error',
          msg: `Unresolved error in the ${this.title} code area`
        });
        this.setTheme(this.errorTheme);
        this.saveLoading = false;
        return;
      }
      this.$emit('save', {
        onSuccess: this.handleSaveSuccess,
        onFailure: this.handleSaveFailure,
        doc: this.editorView.state.doc.toString()
      });
    },
    handleSaveSuccess() {
      this.saveLoading = false;
      this.setTheme(this.viewTheme);
      this.editMode = false;
      this.initialState = markRaw(this.editorView.state);
      this.setReadOnlyRanges([{ fromLine: 1, toLine: 'last' }]);
      const successMessage = this.title ? `${this.title} code area successfully saved` : 'Code area successfully saved';
      this.triggerToast({
        mode: 'success',
        msg: successMessage
      });
    },
    handleSaveFailure(error) {
      this.saveLoading = false;
      const errorMessage = processErrorParent(error);
      this.triggerToast({
        mode: 'error',
        msg: `Error in the ${this.title} code area: ${errorMessage}`
      });
      this.setTheme(this.errorTheme);
    },
    async getDiagnostics(): Promise<Diagnostic[]> {
      const diagnostics = [] as Diagnostic[];
      forceLinting(this.editorView as EditorView);
      await new Promise(resolve => setTimeout(resolve, 200));
      forEachDiagnostic(this.editorView.state as EditorState, diagnostic => {
        if (diagnostic.severity === 'error') {
          diagnostics.push(diagnostic);
        }
      });
      return diagnostics;
    },
    characterLimitExceeded() {
      if (this.characterLimit <= 0) {
        return false;
      }
      if ((this.$refs[this.codeAreaKey] as any)?.charactersUsed > this.characterLimit) {
        this.triggerToast({
          mode: 'error',
          msg: `Character limit exceeded in the ${this.title} code area`
        });
        return true;
      }
    }
  }
});
</script>
